import React from 'react';
import { Box, Typography, Paper, useMediaQuery, useTheme, styled } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// Styled Components
const CustomStepConnector = styled('div')(({ theme }) => ({
  width: 2,
  height: 40,
  backgroundColor: '#e0e0e0',
  marginLeft: 13,
  marginTop: 4,
  marginBottom: 4,
}));

const CustomStepConnectorHorizontal = styled('div')(({ theme }) => ({
  width: 50,
  height: 2,
  backgroundColor: '#e0e0e0',
}));

const CustomStepIcon = ({ active, completed, isMobile }) => {
  const size = isMobile ? 24 : 28;
  const innerSize = isMobile ? 12 : 14;

  if (completed) {
    return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: size }} />;
  }

  if (active) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          border: '3px solid #1976d2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{
            width: innerSize,
            height: innerSize,
            borderRadius: '50%',
            backgroundColor: '#1976d2',
          }}
        />
      </Box>
    );
  }

  return <RadioButtonUncheckedIcon sx={{ color: '#e0e0e0', fontSize: size }} />;
};

const PostSidebar = ({ currentStep, category, propertyType, assetType }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ✅ Dynamic steps based on category and property type
  const getSteps = () => {
    const baseSteps = ['Basic Details', 'Property Details'];

    // Check if amenities should be shown
    const isCommercial = propertyType === 'Commercial';
    const isPlot = assetType === 'plot' || assetType === 'Plot';
    const isAgricultureLand = assetType === 'agriculture-land' || assetType === 'Agriculture Land';
    const isFarmhouse = assetType === 'farm-house' || assetType === 'Farm House';

    // ✅ Add amenities step for:
    // 1. All residential properties (except plot and agricultural land)
    // 2. Commercial farmhouse
    const shouldShowAmenities = (isFarmhouse || !isCommercial) && !isPlot && !isAgricultureLand;
    
    if (shouldShowAmenities) {
      baseSteps.push('Add Amenities');
    }

    // ✅ Use category to determine final step name
    const finalStep = category === 'Rent' ? 'Rent Details' : 'Price Details';
    return [...baseSteps, finalStep];
  };

  const steps = getSteps();

  return (
    <Paper
      elevation={0}
      sx={{
        // ✅ Width: 380px
        width: { xs: '100%', sm: '100%', md: '380px', lg: '380px' },
        minWidth: { md: '380px' },
        maxWidth: { md: '380px' },
        p: 0,
        backgroundColor: '#f8f9fa',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        position: { md: 'sticky' },
        top: { md: 20 },
        alignSelf: 'flex-start',
        // ✅ CRITICAL FIX: Ensure entire sidebar is visible while scrolling
        height: { md: 'fit-content' },
        maxHeight: { md: 'calc(100vh - 40px)' },
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '10px',
          '&:hover': {
            backgroundColor: '#555',
          },
        },
      }}
    >
      {/* ✅ Sticky Header - Always Visible at Top */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          backgroundColor: '#f8f9fa',
          zIndex: 10,
          pt: { xs: 2.5, md: 3.5 },
          pb: { xs: 2, md: 2.5 },
          px: { xs: 2.5, md: 4 },
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Outfit',
            fontWeight: 600,
            fontSize: { xs: '16px', md: '20px' },
            textAlign: 'center',
            width: '100%',
            color: '#1a1a1a',
            letterSpacing: '0.5px',
            lineHeight: 1.3,
          }}
        >
          Post your property Deals
        </Typography>
      </Box>

      {/* ✅ Scrollable Content Container - CENTERED with proper flex */}
      <Box
        sx={{
          width: '100%',
          flex: 1, // ✅ Takes available space
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', // ✅ CENTER ALIGNMENT
          justifyContent: 'flex-start',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 2.5, md: 3.5 },
          pb: { xs: 2.5, md: 4 },
          minHeight: 'min-content', // ✅ Ensures content is fully visible
        }}
      >
        {/* Desktop Stepper - Vertical */}
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: '300px', // ✅ Centered content with max width
              alignItems: 'flex-start',
            }}
          >
            {steps.map((label, index) => (
              <Box key={label} sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CustomStepIcon
                    active={index === currentStep}
                    completed={index < currentStep}
                    isMobile={false}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontSize: '15px',
                      fontWeight: index === currentStep ? 600 : 400,
                      color:
                        index === currentStep
                          ? '#1976d2'
                          : index < currentStep
                          ? '#4caf50'
                          : '#666',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
                {index < steps.length - 1 && <CustomStepConnector />}
              </Box>
            ))}
          </Box>
        )}

        {/* Mobile Stepper - Horizontal */}
        {isMobile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '10px',
              },
            }}
          >
            {steps.map((label, index) => (
              <React.Fragment key={label}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '60px', flex: 1 }}>
                  <CustomStepIcon
                    active={index === currentStep}
                    completed={index < currentStep}
                    isMobile={true}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontSize: '10px',
                      fontWeight: index === currentStep ? 600 : 400,
                      color:
                        index === currentStep
                          ? '#1976d2'
                          : index < currentStep
                          ? '#4caf50'
                          : '#666',
                      mt: 0.8,
                      textAlign: 'center',
                      maxWidth: '70px',
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
                {index < steps.length - 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', pt: '10px', pb: '18px' }}>
                    <CustomStepConnectorHorizontal />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default PostSidebar;